<script setup>
import { computed, onMounted, ref } from 'vue'
import { useAuth } from '@/auth/auth'
import { useCurrentUser } from '@/composables/useCurrentUser.js'
import { MessageUsageKind } from '@/models/messageUsage.js'

const { user, logout } = useAuth()
const { currentUser, currentUserError, isLoadingCurrentUser, refreshCurrentUser } = useCurrentUser()

const operationError = ref('')

const auth = useAuth()

const profileRows = computed(() => [
  { label: 'Name', value: user.value?.name },
  { label: 'Username', value: user.value?.username },
  { label: 'Email', value: user.value?.email },
])

const usageRows = computed(() => [
  {
    label: 'Text messages',
    usage: currentUser.value?.messageUsage?.[MessageUsageKind.TEXT],
  },
  {
    label: 'Audio messages',
    usage: currentUser.value?.messageUsage?.[MessageUsageKind.AUDIO_TTS],
  },
])

const formatUsage = (usage) => {
  if (!usage) {
    return 'Not available'
  }

  const used = usage.usedMessages ?? 0
  const limit = usage.currentMessageLimit

  if (limit == null) {
    return `${used} used`
  }

  return `${used} of ${limit} used`
}

const refreshAccount = async () => {
  operationError.value = ''

  try {
    await refreshCurrentUser()
  } catch (error) {
    operationError.value = error instanceof Error ? error.message : String(error)
  }
}

const changePassword = async () => {
  operationError.value = ''

  try {
    await auth.changePassword()
  } catch (error) {
    operationError.value = error instanceof Error ? error.message : String(error)
  }
}

const handleLogout = () => logout({ redirectPath: '/' })

onMounted(refreshAccount)
</script>

<template>
  <section class="account-page">
    <header class="page-header">
      <div>
        <p class="eyebrow">Account</p>
        <h1>{{ user?.name || 'Your Account' }}</h1>
      </div>
    </header>

    <p v-if="operationError || currentUserError" class="status-message error">
      {{ operationError || currentUserError }}
    </p>

    <div class="account-grid">
      <article class="account-panel">
        <h2>Profile</h2>
        <dl class="info-list">
          <div v-for="row in profileRows" :key="row.label" class="info-row">
            <dt>{{ row.label }}</dt>
            <dd>{{ row.value || 'Not provided' }}</dd>
          </div>
        </dl>
      </article>

      <article class="account-panel">
        <h2>Usage</h2>
        <p v-if="isLoadingCurrentUser" class="status-message">Loading account usage...</p>
        <dl v-else class="info-list">
          <div v-for="row in usageRows" :key="row.label" class="info-row">
            <dt>{{ row.label }}</dt>
            <dd>{{ formatUsage(row.usage) }}</dd>
          </div>
        </dl>
      </article>

      <article class="account-panel operations-panel">
        <h2>Actions</h2>
        <div class="operation-list">
          <button class="operation-button" type="button" @click="changePassword">
            Change password
          </button>
          <button class="operation-button danger" type="button" @click="handleLogout">
            Log out
          </button>
        </div>
      </article>
    </div>
  </section>
</template>

<style scoped>
.account-page {
  padding: 2rem 1.5rem 3rem;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.eyebrow {
  margin: 0 0 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.75rem;
  color: #7acb8a;
}

h1,
h2 {
  margin: 0;
  color: #f6f7f2;
}

h1 {
  font-size: clamp(2rem, 4vw, 3rem);
}

h2 {
  font-size: 1.15rem;
}

.header-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.65rem;
}

.account-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.account-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: linear-gradient(180deg, rgba(39, 55, 45, 0.96), rgba(19, 27, 22, 0.98));
  border: 1px solid rgba(122, 203, 138, 0.18);
  border-radius: 8px;
  padding: 1.2rem;
  color: #eef3eb;
}

.account-panel h2 {
  margin-top: 0;
  color: #9fe2ab;
  height: 1.5rem;
}

.operations-panel {
  grid-column: 1 / -1;
}

.info-list {
  display: grid;
  gap: 0.85rem;
  margin: 0;
}

.info-row {
  display: grid;
  gap: 0.25rem;
  min-width: 0;
}

.info-row dt {
  color: #8fa195;
  font-size: 0.75rem;
  text-transform: uppercase;
}

.info-row dd {
  margin: 0;
  color: #f6f7f2;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.operation-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.action-button,
.operation-button {
  border: 1px solid rgba(174, 224, 182, 0.38);
  background: rgba(255, 255, 255, 0.08);
  color: #eef3eb;
  border-radius: 8px;
  min-height: 2.35rem;
  padding: 0.5rem 0.85rem;
  cursor: pointer;
  font-weight: 700;
  white-space: nowrap;
}

.action-button:hover:not(:disabled),
.operation-button:hover:not(:disabled) {
  border-color: #9fe2ab;
  background: rgba(122, 203, 138, 0.16);
}

.action-button:disabled,
.operation-button:disabled {
  cursor: not-allowed;
  opacity: 0.62;
}

.action-button.primary {
  background: #4caf50;
  border-color: #4caf50;
  color: #08120a;
}

.operation-button.danger {
  border-color: rgba(248, 113, 113, 0.38);
  background: rgba(248, 113, 113, 0.12);
  color: #fecaca;
}

.operation-button.danger:hover:not(:disabled) {
  border-color: #f87171;
  background: rgba(248, 113, 113, 0.2);
}

.status-message {
  margin: 0;
  color: #c3c8be;
}

.status-message.error {
  margin-bottom: 1rem;
  color: #ff9e9e;
  font-weight: 600;
}

@media (max-width: 700px) {
  .account-page {
    padding: 1.2rem 1rem 2rem;
  }

  .page-header,
  .header-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .account-grid {
    grid-template-columns: 1fr;
  }

  .action-button,
  .operation-button {
    width: 100%;
  }
}
</style>
